import { User, Mail, Phone, Camera, Pencil } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';
import { getAssetUrl } from '../../../../../utils/assetUtils';

const AccountProfileSection = () => {
  const {
    formData,
    isEditing,
    renderInput,
    fileInputRef,
    handleLogoClick,
    handleLogoChange
  } = useProfile();

  return (
    <div>
      <div className="border-b border-slate-100 pb-2.5 mb-4">
        <h2 className="font-semibold text-slate-900 text-base">Account profile</h2>
        <p className="text-xs text-slate-500 mt-0.5 font-normal">Personal details &amp; company avatar</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {/* Left Box: Logo and Email Address */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">
          <div>
            <label className="block text-xs font-semibold text-slate-700 tracking-wide mb-2">
              Company Logo / Avatar
            </label>
            <div className="flex items-center gap-3.5">
              {/* Interactive Avatar with Edit overlay */}
              <div
                onClick={handleLogoClick}
                className="relative group cursor-pointer w-14 h-14 rounded-xl bg-slate-50 border-2 border-dashed border-slate-300 hover:border-[#067978] shadow-xs overflow-hidden flex items-center justify-center shrink-0 transition-all duration-200 hover:shadow-md"
                title="Click to change logo"
              >
                {formData.logo ? (
                  <img
                    src={getAssetUrl(formData.logo)}
                    alt="Company Logo"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-slate-400 group-hover:text-[#067978] transition-colors">
                    <User size={24} className="text-slate-300 group-hover:text-[#067978]/60 transition-colors" />
                  </div>
                )}

                {/* Hover Overlay with Camera Icon */}
                <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center text-white gap-0.5 z-10">
                  <Camera size={16} className="text-white drop-shadow-xs" />
                  <span className="text-[8px] font-bold tracking-tight text-white/95 uppercase">Change</span>
                </div>

                {/* Corner Edit Badge */}
                <div className="absolute bottom-0.5 right-0.5 p-0.5 bg-[#067978] group-hover:bg-[#056362] text-white rounded-md shadow-md border border-white transition-transform group-hover:scale-110 z-20 flex items-center justify-center">
                  <Pencil size={9} className="text-white" />
                </div>
              </div>

              <div className="space-y-0.5 min-w-0">
                <h4 className="text-xs font-semibold text-slate-800">Company Logo</h4>
                <p className="text-[11px] text-slate-400 font-medium leading-tight">
                  Click on the avatar to upload or change your logo.
                </p>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleLogoChange}
              />
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100">
            {renderInput("Email Address", "email", formData.email, "email", true, Mail, true, true)}
            <p className="text-xs text-slate-400 mt-1 font-medium">
              * Contact support to change your registered email address.
            </p>
          </div>
        </div>

        {/* Right Box: Full Name and Phone Number */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">
          {renderInput("Full Name", "owner_name", formData.owner_name, "text", !isEditing, User, false, true)}
          <div>
            {renderInput("Phone Number", "phone_number", formData.phone_number, "tel", !isEditing, Phone, false, true)}
            <p className="text-xs text-slate-400 mt-1 font-medium">
              * Primary phone number for account and notifications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountProfileSection;
